import type { ColumnDef, Table } from '@/lib/tanstack-table';
import { DataTableColumnHeader } from '@/components/common/data-table/data-table-column-header';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { ppvDeltaPerUnit } from '../utils/lineBuyTerms';
import { isNeedId } from '../utils/needRows';
import {
  BuyerCostCell,
  BuyTermTextCell,
  PpvCell,
  PpvDeltaCell,
  RevisedCostCell,
} from './BuyTermCells';
import { getCartLineTableMeta as getMeta } from './cartLineTableMeta';

/**
 * The buy-terms line columns (GEN-2943): the buyer (system) cost, the
 * buyer's revised cost and the per-unit variance it makes, and the terms that
 * go on the PO. The saved view supplies each header title; these titles are
 * the fallback for a table without one.
 */
/** The order's real lines: need rows are display-only, so whole-order edits skip them. */
function orderLines(table: Table<AssignmentLine>): AssignmentLine[] {
  return table
    .getRowModel()
    .rows.flatMap(({ original }) =>
      isNeedId(original.suggestionId) ? [] : [original],
    );
}

export const buyTermColumns: ColumnDef<AssignmentLine>[] = [
  {
    id: 'buyerCost',
    accessorFn: (l) => l.buyerUnitCost,
    meta: { align: 'right' },
    header: ({ column, table }) => (
      <DataTableColumnHeader
        column={column}
        title={getMeta(table).gt('Buyer cost', {
          context: 'cart line table column header, system unit cost',
        })}
      />
    ),
    cell: ({ row }) => <BuyerCostCell line={row.original} />,
  },
  {
    id: 'revisedCost',
    accessorFn: (l) => l.buyTerms.revisedUnitCost,
    meta: { align: 'right' },
    size: 88,
    header: ({ column, table }) => (
      <DataTableColumnHeader
        column={column}
        title={getMeta(table).gt('Revised cost', {
          context: 'cart line table column header, one-time special unit cost',
        })}
      />
    ),
    cell: ({ row, table }) => (
      <RevisedCostCell
        line={row.original}
        getOrderLines={() => orderLines(table)}
      />
    ),
  },
  {
    id: 'ppvDelta',
    accessorFn: (l) => ppvDeltaPerUnit(l),
    meta: { align: 'right' },
    header: ({ column, table }) => (
      <DataTableColumnHeader
        column={column}
        title={getMeta(table).gt('PPV Δ', {
          context:
            'cart line table column header, purchase price variance per unit',
        })}
      />
    ),
    cell: ({ row }) => <PpvDeltaCell line={row.original} />,
  },
  {
    id: 'contractNumber',
    accessorFn: (l) => l.buyTerms.contractNumber,
    size: 96,
    header: ({ column, table }) => (
      <DataTableColumnHeader
        column={column}
        title={getMeta(table).gt('Contract #', {
          context: 'cart line table column header, contract number for the PO',
        })}
      />
    ),
    cell: ({ row, table }) => (
      <BuyTermTextCell
        line={row.original}
        field="contractNumber"
        label={getMeta(table).gt('Contract #', {
          context: 'buyer board line input: contract number for the PO',
        })}
      />
    ),
  },
  {
    id: 'ppv',
    accessorFn: (l) => l.buyTerms.ppvReceiveAt,
    size: 144,
    minSize: 144,
    header: ({ column, table }) => (
      <DataTableColumnHeader
        column={column}
        title={getMeta(table).gt('PPV', {
          context:
            'cart line table column header, receive at PO cost or buyer cost',
        })}
      />
    ),
    cell: ({ row, table }) => (
      <PpvCell line={row.original} getOrderLines={() => orderLines(table)} />
    ),
  },
  {
    id: 'poLineComment',
    accessorFn: (l) => l.buyTerms.poLineComment,
    size: 160,
    header: ({ column, table }) => (
      <DataTableColumnHeader
        column={column}
        title={getMeta(table).gt('PO comment', {
          context: 'cart line table column header, comment on the PO line',
        })}
      />
    ),
    cell: ({ row, table }) => (
      <BuyTermTextCell
        line={row.original}
        field="poLineComment"
        label={getMeta(table).gt('PO comment', {
          context: 'buyer board line input: comment on the PO line',
        })}
      />
    ),
  },
];
